import { useState, useMemo } from "react";
import { CheckCircle2, XCircle, Clock, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useSickLeaves,
  useCreateSickLeave,
  useDeleteSickLeave,
} from "../hooks/useLeaves";
import { SICK_LEAVE_STATUS_COLORS } from "../types";

const CAN_REVIEW_ROLES = ["hr", "accountant", "director"];
const CAN_DELETE_ROLES = ["accountant", "director"];

function StatusIcon({ status }: { status: string }) {
  if (status === "verified") return <CheckCircle2 size={14} className="text-green-600" />;
  if (status === "rejected") return <XCircle size={14} className="text-red-500" />;
  return <Clock size={14} className="text-yellow-500" />;
}

export function SickLeavesPage() {
  const { user } = useAuthStore();
  const canReview = CAN_REVIEW_ROLES.includes(user?.role ?? "");
  const canDelete = CAN_DELETE_ROLES.includes(user?.role ?? "");
  const createSickLeave = useCreateSickLeave();
  const deleteSickLeave = useDeleteSickLeave();

  const { data: sickLeavesRes, isLoading } = useSickLeaves();
  const allSickLeaves = sickLeavesRes?.data ?? [];
  const sickLeaves = canReview
    ? allSickLeaves.filter((sl) => sl.employee === user?.id)
    : allSickLeaves;

  const [openL4, setOpenL4] = useState(false);
  const [l4StartDate, setL4StartDate] = useState("");
  const [l4EndDate, setL4EndDate] = useState("");
  const [l4Note, setL4Note] = useState("");
  const [l4Comment, setL4Comment] = useState("");
  const [l4Err, setL4Err] = useState<string | null>(null);

  const l4DaysPreview = useMemo(() => {
    if (!l4StartDate || !l4EndDate || l4EndDate < l4StartDate) return null;
    return (new Date(l4EndDate).getTime() - new Date(l4StartDate).getTime()) / 86_400_000 + 1;
  }, [l4StartDate, l4EndDate]);

  const resetL4 = () => {
    setL4StartDate("");
    setL4EndDate("");
    setL4Note("");
    setL4Comment("");
    setL4Err(null);
  };

  const submitL4 = () => {
    if (!l4StartDate || !l4EndDate) { setL4Err("Data od i do są wymagane."); return; }
    if (l4EndDate < l4StartDate) { setL4Err("Data końcowa nie może być przed datą początkową."); return; }
    createSickLeave.mutate(
      { start_date: l4StartDate, end_date: l4EndDate, note: l4Note, verification_comment: l4Comment },
      {
        onSuccess: () => { setOpenL4(false); resetL4(); },
        onError: () => setL4Err("Błąd podczas zgłaszania L4. Spróbuj ponownie."),
      }
    );
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Zwolnienia lekarskie (L4)
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {canReview
            ? "Twoje zwolnienia lekarskie. Weryfikację zgłoszeń pracowników znajdziesz w 'L4 do zatwierdzenia'."
            : "Zgłoś zwolnienie lekarskie — kadrowa/księgowa/dyrektor potwierdzi je w portalu ZUS."}
        </p>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Stethoscope size={16} className="text-gray-400 dark:text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Moje L4</h2>
        </div>
        <Button onClick={() => { resetL4(); setOpenL4(true); }}>
          <Stethoscope size={15} className="mr-1.5" /> Zgłoś L4
        </Button>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : sickLeaves.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <Stethoscope size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak zgłoszeń L4.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sickLeaves.map((sl) => (
            <div key={sl.id} className="bg-white dark:bg-gray-800 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color={SICK_LEAVE_STATUS_COLORS[sl.status]}>
                      <StatusIcon status={sl.status} />
                      <span className="ml-1">{sl.status_display}</span>
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {formatDate(sl.start_date)} – {formatDate(sl.end_date)}
                    <span className="ml-2 text-gray-400 dark:text-gray-500">
                      ({sl.days_count} {sl.days_count === 1 ? "dzień" : "dni"})
                    </span>
                  </p>
                  {sl.note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{sl.note}</p>
                  )}
                  {sl.verification_comment && (
                    <p className={`text-xs mt-1 font-medium ${sl.status === "rejected" ? "text-red-600" : "text-green-700 dark:text-green-400"}`}>
                      {sl.status === "rejected" ? "Powód odrzucenia: " : "Komentarz weryfikatora: "}
                      {sl.verification_comment}
                    </p>
                  )}
                  {sl.verified_by_name && sl.status === "verified" && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Zweryfikował: {sl.verified_by_name}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {canDelete && (
                    <button
                      onClick={() => {
                        if (confirm("Usunąć to zgłoszenie L4?")) deleteSickLeave.mutate(sl.id);
                      }}
                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 hover:underline"
                    >
                      Usuń
                    </button>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(sl.created_at).toLocaleDateString("pl-PL")}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={openL4} onClose={() => setOpenL4(false)} title="Zgłoszenie L4 — zwolnienie lekarskie">
        <div className="flex flex-col gap-4">
          {canReview && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-md p-3 text-xs text-blue-800 dark:text-blue-300">
              Jako kadrowa/księgowa/dyrektor zgłoszenie zostanie od razu zweryfikowane.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data od <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={l4StartDate}
                onChange={setL4StartDate}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data do <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={l4EndDate}
                min={l4StartDate}
                onChange={setL4EndDate}
              />
            </div>
          </div>
          {l4DaysPreview !== null && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Łącznie: <strong>{l4DaysPreview}</strong> {l4DaysPreview === 1 ? "dzień" : "dni"} kalendarzowych
            </p>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={l4Note}
              onChange={(e) => setL4Note(e.target.value)}
              placeholder="Np. numer zaświadczenia lekarskiego..."
            />
          </div>
          {canReview && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Komentarz weryfikatora <span className="text-gray-400 text-xs">(opcjonalny)</span>
              </label>
              <textarea
                rows={2}
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                value={l4Comment}
                onChange={(e) => setL4Comment(e.target.value)}
                placeholder="Np. zaświadczenie ZUS-ZLA potwierdzone..."
              />
            </div>
          )}
          {l4Err && <p className="text-sm text-red-600">{l4Err}</p>}
          <div className="flex gap-3">
            <Button loading={createSickLeave.isPending} onClick={submitL4}>
              {canReview ? "Zarejestruj L4" : "Zgłoś L4"}
            </Button>
            <Button variant="secondary" onClick={() => setOpenL4(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
